import { MouseEvent, useCallback, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { IAtlasObjectRef, toAtlasRouterPath } from '@common/helpers/atlasUrls'
import {
  cancelHidePreview,
  clearPreviewAnchor,
  getPreviewAnchor,
  scheduleHidePreview,
} from './atlasPreviewAnchor'
import { ATLAS_PREVIEW_TRIGGER } from './useGlobalAtlasLinkPreview'
import { useOpenAtlasPreview } from './useOpenAtlasPreview'

/** Long enough that sweeping the pointer across a message does not flash a card per chip. */
export const PREVIEW_HOVER_DELAY_MS = 400

/** Gestures the browser gives its own meaning to: new tab, new window, download. */
export const isModifiedClick = (event: MouseEvent): boolean =>
  event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0

const canHover = (): boolean =>
  typeof window.matchMedia !== 'function' || window.matchMedia('(hover: hover)').matches

/**
 * The handlers every Atlas record chip spreads on its anchor: hovering shows the record's
 * card, clicking pins it (or unpins it, on the chip it already hangs from). A link with no
 * record behind it navigates in-app — the chips render inside a contentEditable, where the
 * browser suppresses native anchor navigation.
 */
export const useAtlasPreviewTrigger = (objectRef: IAtlasObjectRef | null, url: string) => {
  const navigate = useNavigate()
  const openPreview = useOpenAtlasPreview()
  const openTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const clearOpenTimer = useCallback(() => {
    if (openTimer.current) {
      clearTimeout(openTimer.current)
      openTimer.current = null
    }
  }, [])

  useEffect(() => clearOpenTimer, [clearOpenTimer])

  const onMouseEnter = useCallback(
    (event: MouseEvent<HTMLElement>) => {
      if (!objectRef || !canHover()) return
      const element = event.currentTarget
      const anchor = getPreviewAnchor()
      if (anchor.element === element) {
        cancelHidePreview()
        return
      }
      if (anchor.isPinned) return

      clearOpenTimer()
      openTimer.current = setTimeout(() => {
        openTimer.current = null
        openPreview(objectRef, url, element, { isPinned: false })
      }, PREVIEW_HOVER_DELAY_MS)
    },
    [clearOpenTimer, objectRef, openPreview, url],
  )

  const onMouseLeave = useCallback(
    (event: MouseEvent<HTMLElement>) => {
      clearOpenTimer()
      if (getPreviewAnchor().element === event.currentTarget) {
        scheduleHidePreview()
      }
    },
    [clearOpenTimer],
  )

  const onClick = useCallback(
    (event: MouseEvent<HTMLElement>) => {
      // Chips sit inside rows that are click targets themselves.
      event.stopPropagation()
      if (isModifiedClick(event)) return
      event.preventDefault()
      clearOpenTimer()

      if (!objectRef) {
        void navigate(toAtlasRouterPath(url))
        return
      }
      const anchor = getPreviewAnchor()
      if (anchor.element === event.currentTarget && anchor.isPinned) {
        clearPreviewAnchor()
        return
      }
      openPreview(objectRef, url, event.currentTarget)
    },
    [clearOpenTimer, navigate, objectRef, openPreview, url],
  )

  return { ...ATLAS_PREVIEW_TRIGGER, onMouseEnter, onMouseLeave, onClick }
}
